import { useMemo } from 'react';
import { WHO_MAX_MONTHS, whoSeries, whoValueAt } from '../data/whoReference';
import { ageInMonths } from '../lib/time';
import type { ChildSex, GrowthEntry, GrowthKind } from '../lib/types';

/**
 * Growth chart — the child's own line over the WHO percentile band.
 *
 * Hand-built SVG rather than a charting library: it is one small, very specific
 * plot, and the design system has opinions (token colours, the dashed median,
 * the shaded 3–97% band) that would mostly be fought with a library's defaults.
 *
 * The prototype hardcoded a fixed 0–6 month window. Here the window follows the
 * child, so the plot stays legible as they grow.
 */

const VIEW_W = 300;
const VIEW_H = 200;
const PAD_L = 30;
const PAD_R = 12;
const PAD_T = 12;
const PAD_B = 26;

export type GrowthChartProps = {
  kind: GrowthKind;
  sex: ChildSex;
  birthDate: string;
  /** Measurements of this kind, any order. */
  entries: GrowthEntry[];
};

export function GrowthChart({ kind, sex, birthDate, entries }: GrowthChartProps) {
  const model = useMemo(() => buildChart({ kind, sex, birthDate, entries }), [kind, sex, birthDate, entries]);

  return (
    <>
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={
          kind === 'weight'
            ? '体重のパーセンタイル曲線と記録'
            : '身長のパーセンタイル曲線と記録'
        }
      >
        {/* 3–97% band, then the dashed edges and median over it */}
        <path d={model.bandPath} fill="var(--brand-subtle)" stroke="none" />
        <path
          d={model.p97Path}
          fill="none"
          stroke="var(--sage-300)"
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.7}
        />
        <path
          d={model.p3Path}
          fill="none"
          stroke="var(--sage-300)"
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.7}
        />
        <path
          d={model.medianPath}
          fill="none"
          stroke="var(--sand-400)"
          strokeWidth={1.5}
          strokeDasharray="4 3"
        />

        {/* y axis ticks — the prototype had none, but an unlabelled axis makes
            the band impossible to read against a number you remember */}
        {model.yTicks.map((t) => (
          <text
            key={t.value}
            x={PAD_L - 6}
            y={t.y + 3}
            textAnchor="end"
            fontSize={9}
            fontFamily="var(--font-mono)"
            fill="var(--text-subtle)"
          >
            {t.label}
          </text>
        ))}

        {model.childPath ? (
          <path
            d={model.childPath}
            fill="none"
            stroke="var(--brand)"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ) : null}

        {model.dots.map((d, i) => (
          <circle
            key={i}
            cx={d.cx}
            cy={d.cy}
            r={3.4}
            fill="var(--surface)"
            stroke="var(--brand)"
            strokeWidth={2}
          />
        ))}

        {model.latest ? (
          <circle cx={model.latest.cx} cy={model.latest.cy} r={5} fill="var(--brand)" />
        ) : null}

        {model.xLabels.map((x) => (
          <text
            key={x.months}
            x={x.x}
            y={VIEW_H - 4}
            textAnchor="middle"
            fontSize={9}
            fontFamily="var(--font-mono)"
            fill="var(--text-subtle)"
          >
            {x.label}
          </text>
        ))}
      </svg>

      {model.omitted > 0 ? (
        <div
          className="cjk"
          style={{
            fontSize: 10,
            color: 'var(--text-subtle)',
            textAlign: 'center',
            marginTop: 6,
          }}
        >
          WHOの曲線は0〜{WHO_MAX_MONTHS}か月までです。それ以降の{model.omitted}件はグラフに表示していません（記録は下に残っています）。
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 14, marginTop: 6, justifyContent: 'center', flexWrap: 'wrap' }}>
        <LegendItem swatch={<span style={{ width: 14, height: 2.5, background: 'var(--brand)', borderRadius: 2, display: 'inline-block' }} />}>
          記録
        </LegendItem>
        <LegendItem swatch={<span style={{ width: 14, borderTop: '1.5px dashed var(--sand-400)', display: 'inline-block' }} />}>
          中央値 median
        </LegendItem>
        <LegendItem swatch={<span style={{ width: 14, height: 8, background: 'var(--brand-subtle)', borderRadius: 2, display: 'inline-block' }} />}>
          3–97%
        </LegendItem>
      </div>
    </>
  );
}

function LegendItem({ swatch, children }: { swatch: React.ReactNode; children: React.ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 10,
        color: 'var(--text-muted)',
      }}
    >
      {swatch}
      {children}
    </span>
  );
}

type ChartModel = {
  bandPath: string;
  p3Path: string;
  p50Path: string;
  p97Path: string;
  medianPath: string;
  childPath: string | null;
  dots: { cx: number; cy: number }[];
  latest: { cx: number; cy: number } | null;
  xLabels: { months: number; x: number; label: string }[];
  yTicks: { value: number; y: number; label: string }[];
  /** Measurements past the reference range that this chart cannot place. */
  omitted: number;
};

export function buildChart({
  kind,
  sex,
  birthDate,
  entries,
}: GrowthChartProps): ChartModel {
  const all = entries
    .map((e) => ({ months: ageInMonths(birthDate, e.measuredAt), value: e.value }))
    .filter((p) => Number.isFinite(p.months) && p.months >= 0)
    .sort((a, b) => a.months - b.months);

  /**
   * The WHO tables here stop at 60 months (the standards' 0–5 year range).
   * Measurements past that are dropped rather than clamped — clamping would
   * pile an older child's points on the right edge and imply they belong to
   * the 60-month band. The caller reports the count so the omission is
   * visible instead of silent.
   */
  const points = all.filter((p) => p.months <= WHO_MAX_MONTHS);
  const omitted = all.length - points.length;

  /**
   * Window: always at least 0–6 months (so a newborn's chart is not a single
   * dot on a 2-year axis), otherwise a little past the newest measurement,
   * rounded up to a whole month and capped at the reference table's range.
   */
  const newest = points.length ? points[points.length - 1].months : 0;
  const toMonths = Math.min(WHO_MAX_MONTHS, Math.max(6, Math.ceil(newest + 1)));
  const fromMonths = 0;

  // Vertical range: cover the band across the window, plus the child's own
  // measurements in case they sit outside it, then pad so nothing touches an edge.
  const p3 = whoSeries(kind, sex, 'p3', fromMonths, toMonths);
  const p50 = whoSeries(kind, sex, 'p50', fromMonths, toMonths);
  const p97 = whoSeries(kind, sex, 'p97', fromMonths, toMonths);

  const candidates = [
    ...p3.map((p) => p.value),
    ...p97.map((p) => p.value),
    ...points.map((p) => p.value),
  ];
  const rawMin = Math.min(...candidates);
  const rawMax = Math.max(...candidates);
  const pad = (rawMax - rawMin) * 0.08 || 1;
  const min = rawMin - pad;
  const max = rawMax + pad;

  const x0 = PAD_L;
  const x1 = VIEW_W - PAD_R;
  const yBot = VIEW_H - PAD_B;
  const yTop = PAD_T;

  const mx = (m: number) => x0 + ((m - fromMonths) / (toMonths - fromMonths)) * (x1 - x0);
  const my = (v: number) => yBot - ((v - min) / (max - min)) * (yBot - yTop);

  const line = (series: { months: number; value: number }[]) =>
    'M ' + series.map((p) => `${mx(p.months).toFixed(1)} ${my(p.value).toFixed(1)}`).join(' L ');

  const band =
    'M ' +
    p97.map((p) => `${mx(p.months).toFixed(1)} ${my(p.value).toFixed(1)}`).join(' L ') +
    ' L ' +
    [...p3].reverse().map((p) => `${mx(p.months).toFixed(1)} ${my(p.value).toFixed(1)}`).join(' L ') +
    ' Z';

  const dots = points.map((p) => ({ cx: +mx(p.months).toFixed(1), cy: +my(p.value).toFixed(1) }));

  // x labels at four even steps across the window, whole months.
  const step = (toMonths - fromMonths) / 3;
  const xLabels = [0, 1, 2, 3].map((i) => {
    const months = Math.round(fromMonths + step * i);
    return { months, x: +mx(months).toFixed(1), label: `${months}m` };
  });

  const yTicks = niceTicks(min, max, 4).map((value) => ({
    value,
    y: +my(value).toFixed(1),
    label: kind === 'weight' ? value.toFixed(value % 1 === 0 ? 0 : 1) : String(Math.round(value)),
  }));

  return {
    bandPath: band,
    p3Path: line(p3),
    p50Path: line(p50),
    p97Path: line(p97),
    medianPath: line(p50),
    childPath: dots.length >= 2 ? 'M ' + dots.map((d) => `${d.cx} ${d.cy}`).join(' L ') : null,
    dots,
    latest: dots.length ? dots[dots.length - 1] : null,
    xLabels,
    yTicks,
    omitted,
  };
}

/** Round tick values to something a person would choose (1, 2, 2.5, 5, 10…). */
function niceTicks(min: number, max: number, count: number): number[] {
  const span = max - min;
  if (span <= 0) return [min];
  const rough = span / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const stepMultiple = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  const step = stepMultiple * magnitude;

  const ticks: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max; v += step) {
    ticks.push(+v.toFixed(6));
  }
  return ticks;
}

/** Latest measurement plus how it compares — feeds the summary card. */
export function summarize(
  kind: GrowthKind,
  sex: ChildSex,
  birthDate: string,
  entries: GrowthEntry[],
): { latest: GrowthEntry | null; delta: number | null; medianAtAge: number | null } {
  const sorted = [...entries].sort((a, b) => a.measuredAt.getTime() - b.measuredAt.getTime());
  const latest = sorted.at(-1) ?? null;
  const previous = sorted.at(-2) ?? null;

  return {
    latest,
    delta: latest && previous ? +(latest.value - previous.value).toFixed(2) : null,
    medianAtAge: latest ? whoValueAt(kind, sex, 'p50', ageInMonths(birthDate, latest.measuredAt)) : null,
  };
}
